import React, { useMemo, useState } from 'react';
import { Customer } from '../types';
import { usePos } from '../context/PosContext';
import './CustomerPickerDialog.css';

interface Props {
  // Called with the chosen (existing or newly-created) customer.
  onSelect: (customer: Customer) => void;
  onClose: () => void;
}

// Mirrors ask_customer_details_dialog_fragment_layout.xml — "Customer Details"
// dialog: a search bar, a list of customers to pick from, and an inline
// quick-add (Name + Contact) revealed by the + button.
const CustomerPickerDialog: React.FC<Props> = ({ onSelect, onClose }) => {
  const { customers, addCustomer, formatMoney } = usePos();
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');

  const results = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return customers;
    return customers.filter(
      (c) => c.name.toLowerCase().includes(t) || c.phone?.includes(search.trim())
    );
  }, [customers, search]);

  const saveNew = () => {
    if (!name.trim() || !contact.trim()) {
      alert('Customer name and contact are required.');
      return;
    }
    const saved = addCustomer({
      name: name.trim(),
      phone: contact.trim(),
      creditBalance: 0,
      type: 'CUSTOMER',
    });
    onSelect(saved);
  };

  return (
    <div className="cpd-overlay" onClick={onClose}>
      <div className="cpd" onClick={(e) => e.stopPropagation()}>
        <div className="cpd-head">
          <button className="cpd-back" onClick={onClose}>←</button>
          <span>Customer Details</span>
          <button
            className="cpd-add"
            title="Add new customer"
            onClick={() => {
              setAdding((v) => !v);
              setName(search);
            }}
          >
            ＋
          </button>
        </div>

        <div className="cpd-searchbar">
          <input
            autoFocus
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="cpd-clear" onClick={() => setSearch('')}>✕</button>
          )}
        </div>

        {adding && (
          <div className="cpd-addrow">
            <input
              placeholder="Customer Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              placeholder="Customer Contact"
              value={contact}
              maxLength={10}
              inputMode="tel"
              onChange={(e) => setContact(e.target.value)}
            />
            <button className="cpd-save" onClick={saveNew}>Save</button>
          </div>
        )}

        <div className="cpd-list">
          {results.map((c) => (
            <button key={c.id} className="cpd-item" onClick={() => onSelect(c)}>
              <span className="cpd-item-name">{c.name}</span>
              <span className="cpd-item-meta">
                {c.phone || '—'}
                {(c.creditBalance ?? 0) > 0 && (
                  <span className="cpd-bal"> · Bal {formatMoney(c.creditBalance ?? 0)}</span>
                )}
              </span>
            </button>
          ))}
          {results.length === 0 && (
            <p className="cpd-empty">
              No customers found. Tap ＋ to add {search.trim() ? `"${search.trim()}"` : 'a new customer'}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerPickerDialog;
